<script setup>

import { defineAsyncComponent } from 'vue'

const HeroCarousel = defineAsyncComponent(() => import('./slider.vue'))
const GlobalButton = defineAsyncComponent(() =>
  import('~/components/globals/Button.vue')
)

// Props
const props = defineProps({
  block: {
    type: Object,
    default: () => ({})
  }
})

</script>

<!-- -------------------------------------------------------------------------------------------------
    Template
------------------------------------------------------------------------------------------------- -->

<template>
<section class="hero-carousel">
  <div class="container">

    <div class="hero-carousel__head">

      <div class="hero-carousel__title-container">
        <div v-html="block.heading?.html"
            class="hero-carousel__title"></div>

        <ul v-if="block.headingTable?.length" class="hero-carousel__head-list">
          <li v-for="item in block.headingTable"
            :key="item.id"
            class="hero-carousel__head-list-item-title">
            {{ item.col1 }}
          </li>
        </ul>

      </div>

      <div class="hero-carousel__head-content">
        <p class="text-b1">
          {{ block.description }}
        </p>
        <GlobalButton
          v-if="block.button?.title"
          :url="block.button?.url"
          :title="block.button?.title"
          :buttonSettings="block.buttonSettings"
          bgColor="#000000"
          class="hero-carousel__button"
        />
      </div>

    </div>

    <HeroCarousel
      v-if="block.heroCarouselSlides?.length"
      :slides="block.heroCarouselSlides"
    />
    
  </div>
</section>
</template>

<!-- -------------------------------------------------------------------------------------------------
    Styling
------------------------------------------------------------------------------------------------- -->

<style scoped>

@media (max-width: 813px) {
  .hero-carousel__head {
    min-height: 236px;
  }
}

@media (max-width: 424px) {
  .hero-carousel__head {
    min-height: 284px;
  }
}

@media (max-width: 419px) {
  .hero-carousel__head {
    min-height: 306px;
  }
}

.hero-carousel {
  padding-top: 36px;
  padding-bottom: 26px;
}

.hero-carousel__head {
  display: flex;
  flex-direction: column;
  text-align: center;
  margin-bottom: 44px;
}

.hero-carousel:not(:has(section.carousel)) .hero-carousel__head {
  margin-bottom: 0 !important;
}

.hero-carousel__title-container {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}

.hero-carousel__title :deep(*) {
  @media (max-width: 768px) {
    br {
      display: none;
    }
  }
}

.hero-carousel__head-list {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 4px;
  list-style: none;
  padding-left: 0;
}

.hero-carousel__head-list-item-title {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: 8px;
  background: #F7F5F0;
  min-height: 27px;
  padding: 4px 8px 6px 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 140%; /* 16.8px */
  letter-spacing: -0.24px;
  color: var(--color-gray-800);
  margin-top: 4px;
}

.hero-carousel__head-list-item-title::before {
  content: '';
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: currentColor;
  flex: 0 0 4px;
  margin-bottom: 2px;
}

.hero-carousel__head-list-item-title + .hero-carousel__head-list-item-title {
  margin-left: 4px;
}

.hero-carousel__button {
  margin-top: 32px;
  min-width: 158px;
}


@media (min-width: 1024px) {

  .hero-carousel {
    padding-top: 80px;
    padding-bottom: 30px;
  }

  .hero-carousel__head {
    flex-direction: row;
    justify-content: space-between;
    text-align: left;
    margin-bottom: 64px;
  }

  .hero-carousel__title-container {
    margin-bottom: 0;
    margin-right: 24px;
  }

  .hero-carousel__head-content {
    width: 48%;
    max-width: 572px;
    flex-shrink: 0;
  }

  .hero-carousel__head-content .text-b1 {
    font-size: 20px;
    line-height: 136%; /* 27.2px */
    letter-spacing: -0.4px;
  }

  .hero-carousel__head-list {
    justify-content: flex-start;
    margin-top: 28px;
  }

  .hero-carousel__head-list-item-title {
    font-size: 18px;
    line-height: 138%; /* 24.84px */
    letter-spacing: -0.36px;
    min-height: 36px;
    padding: 5px 16px 6px 16px;
    gap: 6px;
  }

  .hero-carousel__head-list-item-title::before {
    width: 6px;
    height: 6px;
    flex: 0 0 6px;
    margin-bottom: 0;
  }

  .hero-carousel__head-list-item-title + .hero-carousel__head-list-item-title {
    margin-left: 8px;
  }

  .hero-carousel__button {
    min-width: 171px;
  }


}

@media (min-width: 1440px) {

  .hero-carousel__head-content {
    width: calc(572 / 1552 * 100%);
    max-width: 572px;
    padding-top: 12px;
  }

}

</style>

<style>

.page.default-page.--overlapping-header .hero-carousel {
  padding-top: 108px !important;
  padding-bottom: 40px !important;

  @media (min-width: 1440px) {
    padding-top: 152px !important;
    padding-bottom: 80px !important;
  }
}

</style>